import type { SupabaseClient } from '@supabase/supabase-js';
import { normalizeAvatar } from '../config/avatars';
import { LEADERBOARD_LIMIT, type AnyBoardKind, type LeaderboardRow } from '../config/leaderboard';

/**
 * The client side of the leaderboards: four thin wrappers over the RPCs in
 * `20260920090000_leaderboards.sql`.
 *
 * Every call is failure-tolerant in the same way `saveSync` is — a server
 * problem is warned and swallowed, never thrown at the scene, because a board
 * that cannot load must degrade to "unavailable" and leave the menu playable.
 * A `null` return means "the call failed"; an empty array means "no players
 * yet", and the two render differently.
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md §5
 */

let client: SupabaseClient | null = null;

/** Called once from main.ts with whatever `createAccountClient()` produced. */
export function initLeaderboard(accountClient: SupabaseClient | null): void {
  client = accountClient;
}

/**
 * Is there a backend at all? A build with no `VITE_SUPABASE_*` has none, and
 * the episode menu then never draws the Leaderboards tab — the same way the
 * account button never renders.
 */
export function leaderboardAvailable(): boolean {
  return client !== null;
}

/** Signed in right now? Drives the own-rank row and the EDIT AVATAR button. */
export async function leaderboardSignedIn(): Promise<boolean> {
  if (!client) return false;
  try {
    const { data } = await client.auth.getSession();
    return !!data.session;
  } catch {
    return false;
  }
}

interface RawRow {
  rank?: unknown;
  username?: unknown;
  avatar?: unknown;
  value?: unknown;
}

/** Server rows are game data like any other: validated, never trusted as-is. */
function toRow(raw: RawRow): LeaderboardRow | null {
  const rank = Number(raw.rank);
  const value = Number(raw.value);
  if (!Number.isFinite(rank) || rank < 1) return null;
  if (typeof raw.username !== 'string' || raw.username.length === 0) return null;
  return {
    rank: Math.floor(rank),
    username: raw.username,
    avatar: normalizeAvatar(typeof raw.avatar === 'string' ? raw.avatar : ''),
    value: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0,
  };
}

/** The top of a board. `null` on failure, `[]` when nobody has ranked yet. */
export async function fetchTopBoard(kind: AnyBoardKind): Promise<LeaderboardRow[] | null> {
  if (!client) return null;
  try {
    const { data, error } = await client.rpc('leaderboard_top', {
      kind,
      lim: LEADERBOARD_LIMIT,
    });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    return rows.map(toRow).filter((r): r is LeaderboardRow => r !== null);
  } catch (e) {
    console.warn('[leaderboard] top failed', e);
    return null;
  }
}

/**
 * The player's own row, with their true rank however far down it is. `null`
 * when signed out, when the call fails, or when they have no save row yet.
 */
export async function fetchMyRank(kind: AnyBoardKind): Promise<LeaderboardRow | null> {
  if (!client) return null;
  try {
    const { data, error } = await client.rpc('leaderboard_me', { kind });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    return rows.length > 0 ? toRow(rows[0] as RawRow) : null;
  } catch (e) {
    console.warn('[leaderboard] own rank failed', e);
    return null;
  }
}

/** The signed-in player's equipped icon key ('' when none or unavailable). */
export async function fetchMyAvatar(): Promise<string> {
  if (!client) return '';
  try {
    const { data } = await client.auth.getSession();
    const id = data.session?.user.id;
    if (!id) return '';
    const { data: rows, error } = await client
      .from('profiles')
      .select('avatar')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return normalizeAvatar((rows as { avatar?: string } | null)?.avatar);
  } catch (e) {
    console.warn('[leaderboard] avatar read failed', e);
    return '';
  }
}

/**
 * Equip an icon. The allowlist and the "do you actually own that tower" rule
 * are enforced by `set_avatar` in the database, so a refusal here is a real
 * refusal and not a cosmetic one — this returns false and the picker keeps the
 * previous selection rather than lying about what other players will see.
 */
export async function saveAvatar(key: string): Promise<boolean> {
  if (!client) return false;
  try {
    const { error } = await client.rpc('set_avatar', { key });
    if (error) throw error;
    return true;
  } catch (e) {
    console.warn('[leaderboard] set avatar failed', e);
    return false;
  }
}
